import React from 'react';
import { Link } from 'react-router-dom';
import './Breadcrumb.css';

/**
 * Phase 4 breadcrumb. Renders the full category chain returned by
 * /api/categories/path. Each segment is a link to its own nested URL so
 * customers can jump back up the hierarchy with one tap.
 *
 *   <Breadcrumb chain={[{slug:'pokemon',name:'Pokémon',kind:'game'}, ...]} />
 *
 * Includes JSON-LD BreadcrumbList markup for SEO so Google can render rich
 * site-link results.
 */
export default function Breadcrumb({ chain }) {
  if (!Array.isArray(chain) || chain.length === 0) return null;

  // Build cumulative href for each segment: /shop, /shop/pokemon, ...
  const items = chain.map((node, i) => ({
    ...node,
    href: '/shop/' + chain.slice(0, i + 1).map(c => c.slug).join('/'),
  }));

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Shop', item: '/shop' },
      ...items.map((it, i) => ({
        '@type': 'ListItem',
        position: i + 2,
        name: it.name,
        item: it.href,
      })),
    ],
  };

  return (
    <nav className="breadcrumb" aria-label="Breadcrumb">
      <ol>
        <li className="breadcrumb__item">
          <Link to="/shop">Shop</Link>
          <span className="breadcrumb__sep" aria-hidden="true">›</span>
        </li>
        {items.map((it, i) => {
          const isLast = i === items.length - 1;
          return (
            <li key={it.id || it.slug} className="breadcrumb__item">
              {isLast
                ? <span className="breadcrumb__current" aria-current="page">{it.name}</span>
                : <>
                    <Link to={it.href}>{it.name}</Link>
                    <span className="breadcrumb__sep" aria-hidden="true">›</span>
                  </>}
            </li>
          );
        })}
      </ol>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
    </nav>
  );
}
